import { Injectable } from '@angular/core';

import { IMarker as _IMarker } from '@lib/geo/map';
import { SnBadgeConfig } from '@lib/ui/badge';

import { GoogleMap } from '../../../map';
import { IGoogleMarker } from '../interfaces/google-marker.interface';

/**
 * Interface for Google Maps Marker with content property
 * This extends the standard google.maps.Marker to include the content property
 * that is available on AdvancedMarkerElement instances
 */
interface IGoogleMarkerWithContent extends google.maps.Marker {
  content?: HTMLElement;
}

type IMarker = _IMarker<GoogleMap>;

/**
 * Service responsible for badge management operations on Google Map markers.
 * Handles creation, styling, and positioning of various badge types.
 */
@Injectable()
export class GoogleMarkerBadgeService {
  /**
   * Set a badge configuration on a marker
   */
  setBadge(googleMarker: IGoogleMarker, marker: IMarker, badge: SnBadgeConfig): void {
    googleMarker.setBadge(badge);
    marker.badge = badge;
  }

  /**
   * Get badge configuration from a marker
   */
  getBadge(marker: IMarker): SnBadgeConfig {
    return marker.badge;
  }

  /**
   * Create a badge based on marker type and optional badge class
   */
  createBadge(googleMarker: IGoogleMarker, marker: IMarker, badgeClass?: string): void {
    const badgeType = this._determineBadgeType(marker, badgeClass);

    switch (badgeType) {
      case 'cluster':
        this._createClusterBadge(googleMarker, marker);
        break;
      case 'status':
        this._createStatusBadge(googleMarker, marker);
        break;
      case 'selected':
        this._createSelectedBadge(googleMarker, marker);
        break;
      case 'icon':
        this._createIconBadge(googleMarker, marker);
        break;
      case 'custom':
        this._createCustomBadge(googleMarker, marker, badgeClass);
        break;
      case 'standard':
      default:
        this._createStandardBadge(googleMarker, marker);
        break;
    }
  }

  /**
   * Create a selected state badge
   */
  createSelectedBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    this._createSelectedBadge(googleMarker, marker);
    marker.selected = true;
  }

  /**
   * Remove selected state badge
   */
  removeSelectedBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    this._removeSelectedBadge(googleMarker);
    marker.selected = false;
  }

  /**
   * Set badge status with color and position
   */
  setBadgeStatus(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void {
    this._createStatusBadge(googleMarker, marker, status, badgePosition);
  }

  // Private helper methods
  private _determineBadgeType(marker: IMarker, badgeClass?: string): string {
    // Check if it's a cluster marker
    if (marker.metadata?.entityType === 'cluster') {
      return 'cluster';
    }

    // Check if it's a selected marker
    if (marker.selected) {
      return 'selected';
    }

    // Check for custom badge class
    if (badgeClass) {
      if (badgeClass.includes('status')) return 'status';
      if (badgeClass.includes('icon')) return 'icon';
      if (badgeClass.includes('custom')) return 'custom';
    }

    // Check if marker has badge config
    if (marker.badge) {
      return 'standard';
    }

    // Default to standard badge
    return 'standard';
  }

  private _createClusterBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    const badge = document.createElement('span');
    const objectCount = marker.metadata?.customData?.clusterObjects || 0;
    badge.innerText = objectCount.toString();
    badge.classList.add('fs__advanced-marker-cluster-badge');

    this._appendBadgeToMarker(googleMarker, badge);
  }

  private _createStatusBadge(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    status?: string,
    position?: Record<string, unknown>,
  ): void {
    const badge = document.createElement('span');
    badge.classList.add('fs__advanced-marker-status-badge');

    // Set status color if provided
    if (status) {
      badge.style.backgroundColor = status;
    }

    // Set position if provided
    if (position) {
      Object.entries(position).forEach(([pos, offset]) => {
        badge.style[pos] = offset as string;
      });
    }

    this._appendBadgeToMarker(googleMarker, badge);
  }

  private _createSelectedBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    const badge = document.createElement('span');
    badge.innerText = '✔';
    badge.classList.add('fs__advanced-marker-selected-badge');
    badge.id = 'selected-badge';

    this._appendBadgeToMarker(googleMarker, badge);
  }

  private _createIconBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    const badge = document.createElement('span');
    const iconCode = marker.iconLabel?.text || '📍';
    badge.innerHTML = iconCode;
    badge.classList.add('fs__advanced-marker-icon-badge');

    this._appendBadgeToMarker(googleMarker, badge);
  }

  private _createCustomBadge(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    badgeClass?: string,
  ): void {
    const badge = document.createElement('span');
    badge.innerText = marker.title || 'Custom';
    badge.classList.add('fs__advanced-marker-cluster-badge');

    // Add custom class if provided
    if (badgeClass) {
      badge.classList.add(badgeClass);
    }

    // Set default position
    badge.style.bottom = '16px';
    badge.style.right = '-9px';

    this._appendBadgeToMarker(googleMarker, badge);
  }

  private _createStandardBadge(googleMarker: IGoogleMarker, marker: IMarker): void {
    // Use the existing SnBadgeConfig system
    if (marker.badge) {
      googleMarker.setBadge(marker.badge);
    }
  }

  private _removeSelectedBadge(googleMarker: IGoogleMarker): void {
    const marker = googleMarker.getMarker();
    const markerWithContent = marker as IGoogleMarkerWithContent;
    const markerElement = markerWithContent.content;

    if (markerElement) {
      const selectedBadge = markerElement.querySelector('#selected-badge');
      if (selectedBadge) {
        markerElement.removeChild(selectedBadge);
      }
    }
  }

  private _appendBadgeToMarker(googleMarker: IGoogleMarker, badge: HTMLElement): void {
    const marker = googleMarker.getMarker();
    const markerWithContent = marker as IGoogleMarkerWithContent;
    const markerElement = markerWithContent.content;

    if (markerElement) {
      markerElement.appendChild(badge);
    }
  }
}
